import jsQR from 'jsqr';
import * as OTPAuth from 'otpauth';

export interface ParsedTotp {
  secret: string;
  issuer: string;
  account: string;
  algorithm: string;
  digits: number;
  period: number;
  rawUrl: string;
}

/**
 * Pre-processes an ImageData for better QR code detection.
 * Applies grayscale conversion + adaptive binarization to improve contrast,
 * especially for screenshots from dark-themed apps (Google Authenticator, etc).
 */
function preprocessImageData(imageData: ImageData): ImageData {
  const { data, width, height } = imageData;
  const processed = new Uint8ClampedArray(data.length);

  // Step 1: Convert to grayscale and compute average luminance
  const grayscale = new Uint8Array(width * height);
  let totalLuminance = 0;

  for (let i = 0; i < width * height; i++) {
    const offset = i * 4;
    const gray = Math.round(
      data[offset] * 0.299 + data[offset + 1] * 0.587 + data[offset + 2] * 0.114
    );
    grayscale[i] = gray;
    totalLuminance += gray;
  }

  const avgLuminance = totalLuminance / (width * height);

  // Step 2: Binarize using adaptive threshold (global Otsu-like approach)
  // Use the average as threshold, with a bias toward detecting dark QR codes
  const threshold = Math.min(avgLuminance * 1.1, 200);

  for (let i = 0; i < width * height; i++) {
    const offset = i * 4;
    const val = grayscale[i] >= threshold ? 255 : 0;
    processed[offset] = val;
    processed[offset + 1] = val;
    processed[offset + 2] = val;
    processed[offset + 3] = 255;
  }

  return new ImageData(processed, width, height);
}

/**
 * Upscales an ImageData by a given factor using nearest-neighbor interpolation.
 * This helps jsQR detect small QR codes in low-res screenshots.
 */
function upscaleImageData(imageData: ImageData, factor: number): ImageData {
  const { data, width, height } = imageData;
  const newWidth = width * factor;
  const newHeight = height * factor;
  const upscaled = new Uint8ClampedArray(newWidth * newHeight * 4);

  for (let y = 0; y < newHeight; y++) {
    for (let x = 0; x < newWidth; x++) {
      const srcX = Math.floor(x / factor);
      const srcY = Math.floor(y / factor);
      const srcOffset = (srcY * width + srcX) * 4;
      const dstOffset = (y * newWidth + x) * 4;
      upscaled[dstOffset] = data[srcOffset];
      upscaled[dstOffset + 1] = data[srcOffset + 1];
      upscaled[dstOffset + 2] = data[srcOffset + 2];
      upscaled[dstOffset + 3] = data[srcOffset + 3];
    }
  }

  return new ImageData(upscaled, newWidth, newHeight);
}

/**
 * Decodes a QR code from a browser Canvas' ImageData using jsQR.
 * Uses a multi-pass strategy:
 *   1. Try original image with both inversion modes
 *   2. Try preprocessed (binarized) image
 *   3. Try upscaled image (for small QR codes)
 */
export function decodeQrCode(imageData: ImageData): string | null {
  try {
    // Pass 1: Original image with inversion support (handles dark themes)
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'attemptBoth',
    });
    if (code?.data) return code.data;

    // Pass 2: Preprocessed (binarized) image for better contrast
    const binarized = preprocessImageData(imageData);
    const code2 = jsQR(binarized.data, binarized.width, binarized.height, {
      inversionAttempts: 'attemptBoth',
    });
    if (code2?.data) return code2.data;

    // Pass 3: If image is small, upscale and retry
    if (imageData.width < 500 || imageData.height < 500) {
      const scaleFactor = Math.max(2, Math.ceil(600 / Math.min(imageData.width, imageData.height)));
      const upscaled = upscaleImageData(imageData, Math.min(scaleFactor, 4));
      const code3 = jsQR(upscaled.data, upscaled.width, upscaled.height, {
        inversionAttempts: 'attemptBoth',
      });
      if (code3?.data) return code3.data;

      // Pass 4: Upscaled + binarized
      const upscaledBinarized = preprocessImageData(upscaled);
      const code4 = jsQR(upscaledBinarized.data, upscaledBinarized.width, upscaledBinarized.height, {
        inversionAttempts: 'attemptBoth',
      });
      if (code4?.data) return code4.data;
    }

    return null;
  } catch (e) {
    console.error('[QR] Failed to decode image data using jsQR:', e);
    return null;
  }
}

/**
 * Parses an otpauth://totp/... URL and extracts its query parameters.
 */
/**
 * Helper to decode Google Authenticator migration payload (Protobuf formatted).
 */
function decodeMigrationPayload(base64Data: string) {
  try {
    let b64 = base64Data.replace(/\s/g, '');
    while (b64.length % 4 !== 0) {
      b64 += '=';
    }
    const binaryString = atob(b64);
    const buffer = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      buffer[i] = binaryString.charCodeAt(i);
    }

    let offset = 0;
    const otpParametersList: Array<{
      secret: Uint8Array | null;
      name: string;
      issuer: string;
      algorithm: string;
      digits: number;
      period: number;
    }> = [];

    const readVarint = (buf: Uint8Array, off: number) => {
      let value = 0;
      let shift = 0;
      while (off < buf.length) {
        const byte = buf[off++];
        value += (byte & 0x7f) * Math.pow(2, shift);
        if (!(byte & 0x80)) break;
        shift += 7;
      }
      return { value, offset: off };
    };

    const skipField = (buf: Uint8Array, off: number, wireType: number) => {
      if (wireType === 0) {
        return readVarint(buf, off).offset;
      } else if (wireType === 1) {
        return off + 8;
      } else if (wireType === 2) {
        const res = readVarint(buf, off);
        return res.offset + res.value;
      } else if (wireType === 5) {
        return off + 4;
      } else {
        throw new Error('Unsupported wire type: ' + wireType);
      }
    };

    const parseOtpParameters = (buf: Uint8Array) => {
      let off = 0;
      let secret: Uint8Array | null = null;
      let name = '';
      let issuer = '';
      let algorithm = 'SHA1';
      let digits = 6;
      let period = 30;

      while (off < buf.length) {
        const keyResult = readVarint(buf, off);
        const key = keyResult.value;
        off = keyResult.offset;

        const fieldNumber = key >> 3;
        const wireType = key & 0x07;

        if (fieldNumber === 1 && wireType === 2) {
          const lenResult = readVarint(buf, off);
          const len = lenResult.value;
          off = lenResult.offset;
          secret = buf.subarray(off, off + len);
          off += len;
        } else if (fieldNumber === 2 && wireType === 2) {
          const lenResult = readVarint(buf, off);
          const len = lenResult.value;
          off = lenResult.offset;
          name = new TextDecoder().decode(buf.subarray(off, off + len));
          off += len;
        } else if (fieldNumber === 3 && wireType === 2) {
          const lenResult = readVarint(buf, off);
          const len = lenResult.value;
          off = lenResult.offset;
          issuer = new TextDecoder().decode(buf.subarray(off, off + len));
          off += len;
        } else if (fieldNumber === 4 && wireType === 0) {
          const valResult = readVarint(buf, off);
          const val = valResult.value;
          off = valResult.offset;
          if (val === 2) algorithm = 'SHA256';
          else if (val === 3) algorithm = 'SHA512';
          else if (val === 4) algorithm = 'MD5';
          else algorithm = 'SHA1';
        } else if (fieldNumber === 5 && wireType === 0) {
          const valResult = readVarint(buf, off);
          const val = valResult.value;
          off = valResult.offset;
          if (val === 2) digits = 8;
          else digits = 6;
        } else {
          off = skipField(buf, off, wireType);
        }
      }

      return { secret, name, issuer, algorithm, digits, period };
    };

    while (offset < buffer.length) {
      const keyResult = readVarint(buffer, offset);
      const key = keyResult.value;
      offset = keyResult.offset;

      const fieldNumber = key >> 3;
      const wireType = key & 0x07;

      if (fieldNumber === 1 && wireType === 2) {
        const lenResult = readVarint(buffer, offset);
        const len = lenResult.value;
        offset = lenResult.offset;
        const subBuffer = buffer.subarray(offset, offset + len);
        offset += len;
        const otpParam = parseOtpParameters(subBuffer);
        if (otpParam) {
          otpParametersList.push(otpParam);
        }
      } else {
        offset = skipField(buffer, offset, wireType);
      }
    }

    return otpParametersList;
  } catch (e) {
    console.error('[QR] Failed to parse otpauth-migration data', e);
    return null;
  }
}

/**
 * Parses an otpauth://totp/... URL and extracts its query parameters.
 */
export function parseOtpAuthUrl(urlStr: string): ParsedTotp | null {
  try {
    const cleanUrl = urlStr.trim();

    // 1. Check if it's a Google Authenticator migration URL
    if (cleanUrl.toLowerCase().startsWith('otpauth-migration:')) {
      const httpUrl = cleanUrl.replace(/^otpauth-migration:\/*/i, 'http://');
      const url = new URL(httpUrl);
      const dataParam = url.searchParams.get('data');
      if (dataParam) {
        const decodedList = decodeMigrationPayload(dataParam);
        if (decodedList && decodedList.length > 0) {
          const first = decodedList[0];
          if (first.secret) {
            const secretBase32 = new OTPAuth.Secret({
              buffer: first.secret.buffer.slice(
                first.secret.byteOffset,
                first.secret.byteOffset + first.secret.byteLength
              )
            }).base32;
            return {
              secret: secretBase32,
              issuer: first.issuer || 'Google Authenticator Migration',
              account: first.name || 'Conta',
              algorithm: first.algorithm || 'SHA1',
              digits: first.digits || 6,
              period: first.period || 30,
              rawUrl: cleanUrl
            };
          }
        }
      }
      return null;
    }

    // 2. Check if it's a raw base32 secret key (with potential padding and whitespace)
    const potentialSecret = cleanUrl.replace(/[\s=-]/g, '').toUpperCase();
    if (/^[A-Z2-7]{8,128}$/.test(potentialSecret)) {
      return {
        secret: potentialSecret,
        issuer: 'Desconhecido',
        account: 'Conta',
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        rawUrl: `otpauth://totp/Desconhecido:Conta?secret=${potentialSecret}`
      };
    }

    // 3. Parse otpauth:// URL
    if (!cleanUrl.toLowerCase().startsWith('otpauth://') && !cleanUrl.toLowerCase().startsWith('otpauth:')) {
      return null;
    }

    // Convert custom protocol to http:// so the browser's URL parser works perfectly in all cases
    const httpUrl = cleanUrl.replace(/^otpauth:\/*/i, 'http://');
    const url = new URL(httpUrl);

    // Host should be totp (or hotp, but we focus on totp)
    const type = url.hostname.toLowerCase();
    if (type !== 'totp' && type !== 'hotp') {
      // If it's not 'totp' or 'hotp', but we have a secret parameter, we can still accept it!
      if (!url.searchParams.has('secret')) {
        return null;
      }
    }

    const params = url.searchParams;
    let secret = params.get('secret');
    if (!secret) {
      // Try regex fallback for secret parameter
      const secretMatch = cleanUrl.match(/[?&]secret=([^&]+)/i);
      if (secretMatch) {
        secret = secretMatch[1];
      }
    }

    if (!secret) return null;

    // Decode label from pathname
    let pathname = decodeURIComponent(url.pathname);
    if (pathname.startsWith('/')) {
      pathname = pathname.substring(1);
    }

    let issuer = params.get('issuer') || '';
    if (!issuer) {
      const issuerMatch = cleanUrl.match(/[?&]issuer=([^&]+)/i);
      if (issuerMatch) {
        issuer = decodeURIComponent(issuerMatch[1]);
      }
    }

    let account = '';

    if (pathname.includes(':')) {
      const parts = pathname.split(':');
      const parsedIssuer = parts[0].trim();
      const parsedAccount = parts.slice(1).join(':').trim();
      
      if (!issuer) {
        issuer = parsedIssuer;
      }
      account = parsedAccount;
    } else {
      account = pathname.trim();
    }

    const algorithm = params.get('algorithm') || 'SHA1';
    
    let digitsVal = params.get('digits');
    if (!digitsVal) {
      const digitsMatch = cleanUrl.match(/[?&]digits=([^&]+)/i);
      if (digitsMatch) digitsVal = digitsMatch[1];
    }
    const digits = parseInt(digitsVal || '6', 10);

    let periodVal = params.get('period');
    if (!periodVal) {
      const periodMatch = cleanUrl.match(/[?&]period=([^&]+)/i);
      if (periodMatch) periodVal = periodMatch[1];
    }
    const period = parseInt(periodVal || '30', 10);

    return {
      secret: secret.toUpperCase().replace(/[\s=-]/g, ''),
      issuer: issuer.trim() || 'Desconhecido',
      account: account.trim() || 'Conta',
      algorithm: algorithm.toUpperCase(),
      digits: isNaN(digits) ? 6 : digits,
      period: isNaN(period) ? 30 : period,
      rawUrl: cleanUrl
    };
  } catch (e) {
    console.error('[QR] Error parsing OTPAuth URL:', e);
    // Last resort regex parser
    try {
      const cleanUrl = urlStr.trim();
      const secretMatch = cleanUrl.match(/[?&]secret=([^&]+)/i);
      if (secretMatch) {
        const secret = decodeURIComponent(secretMatch[1]).toUpperCase().replace(/[\s=-]/g, '');
        if (/^[A-Z2-7]{8,128}$/.test(secret)) {
          let issuer = 'Desconhecido';
          const issuerMatch = cleanUrl.match(/[?&]issuer=([^&]+)/i);
          if (issuerMatch) {
            issuer = decodeURIComponent(issuerMatch[1]).trim();
          }
          return {
            secret,
            issuer,
            account: 'Conta',
            algorithm: 'SHA1',
            digits: 6,
            period: 30,
            rawUrl: cleanUrl
          };
        }
      }
    } catch (innerErr) {
      console.error('[QR] Regex fallback also failed:', innerErr);
    }
    return null;
  }
}

/**
 * Generates the current TOTP token locally using the otpauth library.
 */
export function generateTotpCode(secret: string, options?: { algorithm?: string; digits?: number; period?: number }): string {
  try {
    // Strip padding and whitespace before passing to OTPAuth
    const cleanSecret = secret.replace(/[\s=-]/g, '').toUpperCase();
    if (!cleanSecret) return '';

    const totp = new OTPAuth.TOTP({
      issuer: 'Lictor',
      label: 'Credencial',
      algorithm: options?.algorithm || 'SHA1',
      digits: options?.digits || 6,
      period: options?.period || 30,
      secret: cleanSecret
    });

    return totp.generate();
  } catch (e) {
    console.error('[QR] Failed to generate local TOTP code:', e);
    return '';
  }
}

/**
 * Returns the remaining seconds in the current TOTP cycle.
 */
export function getTotpRemainingSeconds(period = 30): number {
  return period - (Math.floor(Date.now() / 1000) % period);
}
